/**
 * YourCircle — Horizontal scrolling carousel of updates from linked users
 *
 * Designed for the Bento Box Dashboard (bottom-right quadrant).
 * Shows recent posts from users you've linked with in this club.
 * If no links yet, shows a prompt to start linking.
 */

import { useMemo } from 'react';
import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useCircleFeed, useMyLinks } from './queries';

interface YourCircleProps {
  clubId: string;
  theme: ClubTheme;
  onPostClick?: (post: SocialPost) => void;
}

export default function YourCircle({ clubId, theme, onPostClick }: YourCircleProps) {
  const { data: links, isLoading: linksLoading } = useMyLinks(clubId, 'link');
  const { data: circleFeed, isLoading: feedLoading } = useCircleFeed(clubId);

  const posts = useMemo(() => {
    if (!circleFeed?.pages) return [];
    return circleFeed.pages.flatMap((p) => p.feed).slice(0, 10);
  }, [circleFeed]);

  const isLoading = linksLoading || feedLoading;
  const hasLinks = (links?.length ?? 0) > 0;

  // Empty state: no links yet
  if (!isLoading && !hasLinks) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center rounded-2xl p-6 text-center"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <div className="text-3xl mb-2">🔗</div>
        <h3 className="text-sm font-semibold mb-1" style={{ color: theme.colors.text }}>
          Your Circle
        </h3>
        <p className="text-xs max-w-[200px]" style={{ color: theme.colors.muted }}>
          Link with members to see their updates here. Visit the member directory to get started.
        </p>
      </div>
    );
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div
        className="flex h-full flex-col rounded-2xl p-4"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <h3 className="text-sm font-semibold mb-3" style={{ color: theme.colors.text }}>
          Your Circle
        </h3>
        <div className="flex gap-3 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 w-48 shrink-0 animate-pulse rounded-xl"
              style={{ backgroundColor: `${theme.colors.muted}15` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // No posts from links
  if (posts.length === 0) {
    return (
      <div
        className="flex h-full flex-col rounded-2xl p-4"
        style={{
          backgroundColor: theme.colors.surface,
          border: `1px solid ${theme.colors.muted}15`,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ color: theme.colors.text }}>
            Your Circle
          </h3>
          <span className="text-[10px] font-medium" style={{ color: theme.colors.muted }}>
            {links?.length ?? 0} linked
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-xs" style={{ color: theme.colors.muted }}>
            No recent posts from your circle
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col rounded-2xl p-4"
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.muted}15`,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: theme.colors.text }}>
          Your Circle
        </h3>
        <span className="text-[10px] font-medium" style={{ color: theme.colors.muted }}>
          {links?.length ?? 0} linked
        </span>
      </div>

      {/* Horizontal scroll of post cards */}
      <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-thin">
        {posts.map((post) => (
          <CirclePostCard
            key={post.id}
            post={post}
            theme={theme}
            onClick={() => onPostClick?.(post)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Mini post card for the carousel ──────────────────────────────

interface CirclePostCardProps {
  post: SocialPost;
  theme: ClubTheme;
  onClick?: () => void;
}

function CirclePostCard({ post, theme, onClick }: CirclePostCardProps) {
  const authorName = post.author?.full_name || 'Unknown';
  const avatarUrl = post.author?.avatar_url;
  const bodyPreview = (post.body || '').slice(0, 80);
  const timeAgo = formatTimeAgo(post.created_at);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-52 shrink-0 flex-col gap-2 rounded-xl p-3 text-left transition-all hover:scale-[1.02]"
      style={{
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.muted}15`,
      }}
    >
      {/* Author row */}
      <div className="flex items-center gap-2">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-6 w-6 rounded-full object-cover"
          />
        ) : (
          <div
            className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold"
            style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}
        <span
          className="text-[11px] font-semibold truncate flex-1"
          style={{ color: theme.colors.text }}
        >
          {authorName}
        </span>
        <span className="text-[9px] shrink-0" style={{ color: theme.colors.muted }}>
          {timeAgo}
        </span>
      </div>

      {/* Body preview */}
      <p
        className="text-[11px] leading-relaxed line-clamp-3"
        style={{ color: theme.colors.muted }}
      >
        {bodyPreview}{bodyPreview.length >= 80 ? '...' : ''}
      </p>
    </button>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function formatTimeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
